import { getEnvironmentConfig } from './environments';
import { getRuntimeConfig } from '../runtimeConfig.js';
import { getViteEnvString } from '../shared/vite/readViteMetaEnv.js';

function readBoolFlag(name: string, fallback: boolean): boolean {
  if (typeof process !== 'undefined' && process.env && process.env[name] !== undefined) {
    return process.env[name] === 'true';
  }
  try {
    const env = (globalThis as { import?: { meta?: { env?: Record<string, string> } } }).import?.meta?.env;
    if (env && env[name] !== undefined) {
      return env[name] === 'true';
    }
  } catch {
    // noop
  }
  return fallback;
}

function readNestedBoolean(value: unknown): boolean | undefined {
  if (typeof value === 'boolean') {
    return value;
  }
  if (typeof value === 'object' && value !== null && 'enabled' in value) {
    const enabled = (value as { enabled?: unknown }).enabled;
    return typeof enabled === 'boolean' ? enabled : undefined;
  }
  return undefined;
}

export const lifecycleFeatureFlags = {
  deactivateByDefault: readBoolFlag('LIFECYCLE_DEACTIVATE_BY_DEFAULT', true),
  /**
   * Kill-switch for Mode B / permanent delete.
   * Default **false** to match backend lifecycle Zod (`enablePermanentDelete`).
   * Prefer preflight `enablePermanentDelete` / `modeBAllowed` when present (SSOT).
   */
  enablePermanentDelete: readBoolFlag('LIFECYCLE_ENABLE_PERMANENT_DELETE', false),
  requireExportBeforeDelete: readBoolFlag('LIFECYCLE_REQUIRE_EXPORT_BEFORE_DELETE', true),
  showLifecycleActions: getEnvironmentConfig().showDebugInfo,
  /**
   * Owner org self-service (pause / resume / closure-request / lifecycle GET).
   * Default **true** — matches backend PR-8 `LIFECYCLE_OWNER_ORG_SELF_SERVICE` (FI-12 / G15).
   * When OFF: Owner lifecycle HTTP returns 404; Account Settings Owner cards stay hidden.
   */
  ownerOrgSelfService: readBoolFlag('LIFECYCLE_OWNER_ORG_SELF_SERVICE', true),
};

/**
 * Products catalog visual flags (deploy-scoped).
 * Default **on** — docked split-pane; set `VITE_PRODUCTS_SPLIT_PANE=false` for overlay drawer rollback (AC-MD-07).
 */
export interface ProductsVisualFeatureFlags {
  readonly splitPaneEnabled: boolean;
}

export function getProductsVisualFeatureFlags(): ProductsVisualFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        products?: {
          splitPane?: boolean | { enabled?: boolean };
        };
      }
    | undefined;

  const fromRuntime = readNestedBoolean(runtimeConfig?.products?.splitPane);
  if (fromRuntime !== undefined) {
    return { splitPaneEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_PRODUCTS_SPLIT_PANE');
  return {
    splitPaneEnabled: vite === undefined || vite === '' ? true : vite !== 'false',
  };
}

/**
 * Products catalog scale flags (P3-4 / AC-SCALE-01).
 * Default **off** — client-side list slice until explicitly enabled for large catalogs.
 */
export interface ProductsScaleFeatureFlags {
  readonly serverPaginationEnabled: boolean;
}

export function getProductsScaleFeatureFlags(): ProductsScaleFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        products?: {
          serverPagination?: boolean | { enabled?: boolean };
        };
      }
    | undefined;

  const fromRuntime = readNestedBoolean(runtimeConfig?.products?.serverPagination);
  if (fromRuntime !== undefined) {
    return { serverPaginationEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_PRODUCTS_SERVER_PAGINATION');
  return {
    serverPaginationEnabled: vite === 'true',
  };
}

/**
 * Physical kiosk sensor tiles (battery, temperature, printer, QR).
 * Default **hidden** — no hardware fleet yet; set `VITE_KIOSK_SENSORS_HIDE=false` to show.
 */
export interface KioskSensorsFeatureFlags {
  readonly hideSensors: boolean;
}

export function getKioskSensorsFeatureFlags(): KioskSensorsFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        kiosks?: {
          hideSensors?: boolean;
        };
      }
    | undefined;

  const fromRuntime = runtimeConfig?.kiosks?.hideSensors;
  if (typeof fromRuntime === 'boolean') {
    return { hideSensors: fromRuntime };
  }

  const vite = getViteEnvString('VITE_KIOSK_SENSORS_HIDE');
  return {
    hideSensors: vite === undefined || vite === '' ? true : vite !== 'false',
  };
}

/**
 * Onboarding Needs STEP 1 — donations / both options.
 * Default **hidden** until donations infra is ready; set `VITE_ONBOARDING_DONATIONS_ENABLED=true` to show.
 */
export interface OnboardingDonationsFeatureFlags {
  readonly donationsEnabled: boolean;
}

export function getOnboardingDonationsFeatureFlags(): OnboardingDonationsFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        onboarding?: {
          donationsEnabled?: boolean;
        };
      }
    | undefined;

  const fromRuntime = runtimeConfig?.onboarding?.donationsEnabled;
  if (typeof fromRuntime === 'boolean') {
    return { donationsEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_ONBOARDING_DONATIONS_ENABLED');
  return {
    donationsEnabled: vite === 'true',
  };
}

/**
 * Account Settings demo-tour hub CTA.
 * Default **hidden**; set `VITE_DEMO_TOUR_ENABLED=true` to show.
 */
export interface DemoTourFeatureFlags {
  readonly demoTourEnabled: boolean;
}

export function getDemoTourFeatureFlags(): DemoTourFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        demoTour?: {
          enabled?: boolean;
        };
      }
    | undefined;

  const fromRuntime = runtimeConfig?.demoTour?.enabled;
  if (typeof fromRuntime === 'boolean') {
    return { demoTourEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_DEMO_TOUR_ENABLED');
  return {
    demoTourEnabled: vite === 'true',
  };
}

/**
 * Enterprise nav — Platby a párování (payments-hub).
 * Default **hidden** even when entitlement is ON; set `VITE_PAYMENTS_HUB_NAV_ENABLED=true` to show.
 */
export interface PaymentsHubNavFeatureFlags {
  readonly paymentsHubNavEnabled: boolean;
}

export function getPaymentsHubNavFeatureFlags(): PaymentsHubNavFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        paymentsHubNav?: {
          enabled?: boolean;
        };
      }
    | undefined;

  const fromRuntime = runtimeConfig?.paymentsHubNav?.enabled;
  if (typeof fromRuntime === 'boolean') {
    return { paymentsHubNavEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_PAYMENTS_HUB_NAV_ENABLED');
  return {
    paymentsHubNavEnabled: vite === 'true',
  };
}

/**
 * Enterprise nav — paid Explore only (`customer-explore` / analytics_explore surfaces).
 * Default **hidden** even when entitlement is ON; set `VITE_ANALYTICS_EXPLORE_NAV_ENABLED=true` to show.
 * Does **not** gate FREE Analytika (`analytics` / analytics_basic), Přehled, Zásoby, or Transakce.
 */
export interface AnalyticsExploreNavFeatureFlags {
  readonly analyticsExploreNavEnabled: boolean;
}

export function getAnalyticsExploreNavFeatureFlags(): AnalyticsExploreNavFeatureFlags {
  const runtimeConfig = getRuntimeConfig() as
    | {
        analyticsExploreNav?: {
          enabled?: boolean;
        };
      }
    | undefined;

  const fromRuntime = runtimeConfig?.analyticsExploreNav?.enabled;
  if (typeof fromRuntime === 'boolean') {
    return { analyticsExploreNavEnabled: fromRuntime };
  }

  const vite = getViteEnvString('VITE_ANALYTICS_EXPLORE_NAV_ENABLED');
  return {
    analyticsExploreNavEnabled: vite === 'true',
  };
}

/**
 * Deploy-scoped Vite gate for enterprise nav item ids (HARD-HIDE before entitlement).
 * Returns false for payments-hub / customer-explore when the matching FF is off.
 * FREE Analytika (`analytics`) is never deploy-gated by the Explore FF.
 */
export function isEnterpriseNavItemDeployEnabled(itemId: string): boolean {
  if (itemId === 'payments-hub') {
    return getPaymentsHubNavFeatureFlags().paymentsHubNavEnabled;
  }
  if (itemId === 'customer-explore') {
    return getAnalyticsExploreNavFeatureFlags().analyticsExploreNavEnabled;
  }
  return true;
}

